import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { apiService, apiClient } from "../../../services/apiService.js";
import CommonChip from "../../commonComponents/commonChip/CommonChip.js";
import DraggableChip from "./DragableChip.js";
import formatText from "../../commonComponents/formatText/FormatText.js";
import removeImg from "../../../assets/images/remove-dark-icon.svg";
import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import requestOtpImg from "../../../assets/images/request-otp.svg";
import rightImg from "../../../assets/images/right-white-icon.svg";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Turnstile } from "@marsidev/react-turnstile";

import Header from "../../commonComponents/header/Header.js";
import Footer from "../../commonComponents/footer/Footer.js";

import authService from "../../../services/authService.js";
import removeDarkImg from "../../../assets/images/remove-dark-icon.svg";
// Add these alongside existing imports
import { useNavigate } from "react-router-dom"; // already imported? check — add if not
import LoadingDialog from "../../commonComponents/loader/LoadingDialog.js";
import { maskPhoneNumber } from "../../commonComponents/helperFunctions/HelperFunction.js";
import interviewAuthService from "../../../services/interviewAuthService.js";
// calculateAge removed – answer serialisation now handled inside handleSubmit
import removeWhiteImg from "../../../assets/images/remove-icon.svg"; // for Snackbar close btn
import CountBox from "./CountBox.js";
import { Country, State } from "country-state-city";
import CustomDatePicker from "../../commonComponents/CustomDatePicker.js";
// ─────────────────────────────────────────────
// Country / state lists via country-state-city
// ─────────────────────────────────────────────
const ALL_COUNTRIES = Country.getAllCountries().map((c) => c.name);

const getStatesForCountryCode = (isoCode) =>
  State.getStatesOfCountry(isoCode).map((s) => s.name);

const FIELD_OPTIONS = {
  countryList: ALL_COUNTRIES,
  indianStateList: getStatesForCountryCode("IN"),
  usStateList: getStatesForCountryCode("US"),
  canadianStateList: getStatesForCountryCode("CA"),
  australianStateList: getStatesForCountryCode("AU"),
};

// List-backed dropdown types — resolved automatically
const LIST_TYPES = [
  "countryList",
  "indianStateList",
  "usStateList",
  "canadianStateList",
  "australianStateList",
];

// ─────────────────────────────────────────────
// Map new JSON data_type → internal field type
// ─────────────────────────────────────────────
const DATA_TYPE_MAP = {
  number: "number",
  single_date: "singleDate",
  multiple_option_single_selection: "multipleOptionsSingleSelection",
  multiple_option_multi_selection: "multipleOptionsMultipleSelections",
  text_multiple_entries: "textOnlyMultipleEntries",
  currency: "currency",
  boolean: "checkbox",
  list_of_values_text: "listOfValuesText",
  list_of_values_numbers: "listOfValuesNumbers",
  text: "textarea",
  phone: "phone",
  date_range: "dateRange",
  multiple_dates: "multipleDates",
  multiple_options_multiple_selections: "multipleOptionsMultipleSelections",
  sortable_chip_input: "sortableChipInput",
  // country / state list aliases (all spellings from JSON)
  countries_list: "countryList",
  country_list: "countryList",
  us_states: "usStateList",
  us_state_list: "usStateList",
  indian_states: "indianStateList",
  indian_state_list: "indianStateList",
  canadian_states: "canadianStateList",
  canadian_state_list: "canadianStateList",
  austrailian_states: "australianStateList", // matches typo in JSON
  australian_states: "australianStateList",
  australian_state_list: "australianStateList",
};

/**
 * Derives a stable semantic ID from data_type + label content.
 * This is completely independent of numeric id, display_order, or position —
 * it only cares about what the field IS (its type + what it says).
 */
function getSemanticId(q) {
  const label = (q.label || "").toLowerCase();
  const dtype = (q.data_type || "").toLowerCase();

  if (dtype === "single_date" && label.includes("birth")) return "dob";
  if (dtype === "multiple_option_single_selection" && label.includes("gender"))
    return "gender";
  if (dtype === "text_multiple_entries" && label.includes("language"))
    return "language";
  if (dtype === "number" && label.includes("experience")) return "experience";
  if (dtype === "currency" && label.includes("current")) return "currentCTC";
  if (dtype === "currency" && label.includes("expected")) return "expectedCTC";
  if (dtype === "boolean" && (label.includes("home") || label.includes("wfh")))
    return "isWFH";
  if (dtype === "text_multiple_entries" && label.includes("location"))
    return "preferred_locations";
  if (
    (dtype === "list_of_values_text" || dtype === "dropdown") &&
    label.includes("ethnicity")
  )
    return "ethnicity";
  if (label.includes("referred")) return "referred";
  if (label.includes("related")) return "related";
  // Fall back to a stable key for any new/custom fields
  return `field_${q.id}`;
}

/**
 * Converts one entry from the `questions` array into the internal
 * field shape that the rest of Welcome.js already expects.
 */
function normalizeField(q) {
  const rawType = (q.data_type || "").toLowerCase();

  // Derive semantic id first — used below to decide sortableChipInput
  const semanticId = getSemanticId(q);

  // preferred_locations is text_multiple_entries but needs the sortable/draggable
  // renderer. Identified by semantic meaning, not by numeric id or display_order.
  let internalType =
    rawType === "text_multiple_entries" && semanticId === "preferred_locations"
      ? "sortableChipInput"
      : DATA_TYPE_MAP[rawType] || rawType;

  // Flatten options.config:
  //   [{label, default}]  → string[]   (radio / checkbox-group)
  //   [1, 2, 3, ...]      → string[]   (listOfValuesNumbers dropdown)
  const configArray = q.options?.config || [];
  const flatOptions = configArray.length
    ? configArray.map((o) =>
        typeof o === "object" && o !== null ? o.label : String(o),
      )
    : [];

  // Default value for single-selection comes from config[].default
  const defaultOption = Array.isArray(configArray)
    ? configArray.find((o) => o?.default === true)
    : null;

  return {
    id: semanticId,
    numericId: q.id,
    label: q.label,
    description: q.description || null,
    placeholder: q.placeholder || null,
    suffix: q.suffix || null,
    prefix: q.prefix || null,
    type: internalType,
    order: q.display_order,
    required: q.is_required ?? false,
    options: flatOptions,
    defaultValue: defaultOption?.label ?? null,
    // min/max: numeric for number fields, date-string for date fields
    min: q.options?.validation?.min ?? null,
    max: q.options?.validation?.max ?? null,
    default_id: q.default_id,
  };
}

/**
 * Reads the template from sessionStorage (stored there by OTP.js after
 * verify-otp succeeds) and returns normalized fields.
 * Falls back to [] if nothing is stored yet.
 */
function loadNormalizedFieldsFromSession() {
  try {
    const raw = sessionStorage.getItem("screenup_form_template");
    if (!raw) return [];
    const template = JSON.parse(raw);
    return (template.questions || template.fields || []).map(normalizeField);
  } catch {
    return [];
  }
}

// const currenciesOption = [
//   { code: "USD", name: "United States Dollar", symbol: "$" },
//   { code: "INR", name: "Indian Rupee", symbol: "₹" },
//   { code: "EUR", name: "Euro", symbol: "€" },
//   { code: "GBP", name: "British Pound Sterling", symbol: "£" },
//   { code: "AUD", name: "Australian Dollar", symbol: "A$" },
//   { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
//   { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
//   { code: "JPY", name: "Japanese Yen", symbol: "¥" },
// ];
const currenciesOption = [
  { code: "USD", name: "United States Dollar", symbol: "$" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound Sterling", symbol: "£" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
  { code: "CHF", name: "CHF - Swiss Franc", symbol: "Fr" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
];

const ethnicityOptions = [
  { id: 1, name: "Hispanic or Latino" },
  { id: 2, name: "White" },
  { id: 3, name: "Black or African American" },
  { id: 4, name: "Native American or Alaska Native" },
  { id: 5, name: "Asian" },
  { id: 6, name: "Native Hawaiian or Pacific Islander" },
  { id: 7, name: "Middle Eastern or North African" },
  { id: 8, name: "Mixed or Multi-Racial" },
  { id: 9, name: "Prefer not to say" },
  { id: 10, name: "South Asian" },
  { id: 11, name: "East Asian" },
  { id: 12, name: "Southeast Asian" },
  { id: 13, name: "Caribbean" },
  { id: 14, name: "Sub-Saharan African" },
  { id: 15, name: "Central Asian" },
  { id: 16, name: "Indigenous peoples of the Americas" },
];

// ─────────────────────────────────────────────
// Derive initial form state from JSON config
// ─────────────────────────────────────────────
function buildInitialValues(fields) {
  const values = {};
  fields.forEach((field) => {
    switch (field.type) {
      case "multiChipInput":
      case "sortableChipInput":
      case "textOnlyMultipleEntries":
      case "multipleOptionsMultipleSelections":
      case "listOfValuesNumbers":
      case "multipleDates":
        values[field.id] = [];
        break;
      case "checkbox":
        values[field.id] = false;
        break;
      case "currency":
        values[field.id] = { amount: "", currencyCode: "INR" };
        break;
      case "dateRange":
        values[field.id] = { from: "", to: "" };
        break;
      default:
        values[field.id] = "";
    }
  });
  return values;
}

// ─────────────────────────────────────────────
// Validation helper — only required fields
// ─────────────────────────────────────────────
// AFTER
function validateField(field, value) {
  if (!field.required && field.min == null && field.max == null) return null;

  switch (field.type) {
    case "multiChipInput":
    case "sortableChipInput":
    case "textOnlyMultipleEntries":
    case "multipleOptionsMultipleSelections":
    case "listOfValuesNumbers":
    case "multipleDates": {
      if (field.required && Array.isArray(value) && value.length === 0)
        return `${field.label} is required.`;
      if (field.min != null && value.length < field.min)
        return `Please add at least ${field.min} entry.`;
      if (field.max != null && value.length > field.max)
        return `You can add at most ${field.max} entries.`;
      return null;
    }
    case "checkbox":
      // return value === undefined || value === null || value === ""
      //   ? `${field.label} must be checked.`
      //   : null;
      return null;

    case "currency":
      if (field.required && !value?.amount)
        return `${field.label} is required.`;
      if (value?.amount && !value?.currencyCode)
        return `Please select currency for ${field.label}.`;
      return null;
    case "dateRange":
      return !value?.from || !value?.to
        ? `${field.label} requires both From and To dates.`
        : null;
    case "date":
    case "singleDate": {
      if (field.required && (!value || value.trim() === ""))
        return `${field.label} is required.`;
      if (value) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = today.toISOString().split("T")[0];

        // Detect whether min/max are "today", a YYYY-MM-DD string, or a numeric age offset
        const isDateLike = (v) =>
          v === "today" ||
          (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v));

        if (isDateLike(field.min) || isDateLike(field.max)) {
          // Direct date comparison (e.g. "When are you planning to leave")
          const selected = new Date(value + "T00:00:00");
          const minDate =
            field.min === "today"
              ? today
              : field.min != null
                ? new Date(field.min + "T00:00:00")
                : null;
          const maxDate =
            field.max === "today"
              ? today
              : field.max != null
                ? new Date(field.max + "T00:00:00")
                : null;

          if (minDate && selected < minDate)
            return `Date cannot be before ${field.min === "today" ? "today" : field.min}.`;
          if (maxDate && selected > maxDate)
            return `Date cannot be after ${field.max === "today" ? "today" : field.max}.`;
        } else {
          // Numeric age-based validation (e.g. Date of Birth)
          const dob = new Date(value + "T00:00:00");
          let age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();
          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < dob.getDate())
          )
            age--;
          if (field.min != null && age < field.min)
            return `You must be at least ${field.min} years old.`;
          if (field.max != null && age > field.max)
            return `Age must be ${field.max} years or below.`;
        }
      }
      return null;
    }
    case "number":
    case "listOfValuesNumbers": {
      if (field.required && (!value || value.toString().trim() === ""))
        return `${field.label} is required.`;
      const num = parseFloat(value);
      if (!isNaN(num)) {
        if (field.min != null && num < field.min)
          return `${field.label} must be at least ${field.min}.`;
        if (field.max != null && num > field.max)
          return `${field.label} must be at most ${field.max}.`;
      }
      return null;
    }
    default:
      return !value || value.toString().trim() === ""
        ? `${field.label} is required.`
        : null;
  }
}
const Snackbar = ({ messages, onClose, duration = 5000, type = "error" }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (messages?.length > 0) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(onClose, 300);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [messages, onClose, duration]);

  if (!messages?.length) return null;

  const bgColor = type === "success" ? "bg-green-600" : "bg-red-600";

  return (
    <div
      className={`fixed top-4 left-1/2 -translate-x-1/2 ${bgColor} text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg z-[9999] w-[90%] max-w-md transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
      }`}
    >
      <div className="flex items-center justify-between">
        {messages.length === 1 ? (
          <p className="text-primaryFontSize">{messages[0]}</p>
        ) : (
          <div>
            <p className="text-primaryFontSize font-medium">
              {type === "error"
                ? "Please fix the following errors:"
                : "Success"}
            </p>
            <ul className="text-primaryFontSize space-y-1">
              {messages.map((msg, i) => (
                <li key={i}>• {msg}</li>
              ))}
            </ul>
          </div>
        )}
        <button
          onClick={() => {
            setVisible(false);
            setTimeout(onClose, 300);
          }}
          className="ml-4 text-white hover:text-gray-200"
        >
          <img src={removeWhiteImg} alt="remove" className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};
// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
export default function Welcome() {
  const isCaptchaDisabled = process.env.REACT_APP_DISABLE_CAPTCHA === "true";

  // ── Load form template from sessionStorage (written by OTP.js after verify) ──
  const [normalizedFields, setNormalizedFields] = useState(
    loadNormalizedFieldsFromSession,
  );

  const sortedFields = useMemo(
    () => [...normalizedFields].sort((a, b) => a.order - b.order),
    [normalizedFields],
  );
  const [formValues, setFormValues] = useState(() =>
    buildInitialValues(normalizedFields),
  );
  const [inputValues, setInputValues] = useState({});
  const [currencyMasterFieldId, setCurrencyMasterFieldId] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const [otpVerified, setOtpVerified] = useState(false);
  const [otpStep, setOtpStep] = useState("phone");
  const [otpValue, setOtpValue] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpBoxes, setOtpBoxes] = useState(["", "", "", "", "", ""]);
  const otpInputRefs = useRef([]);
  const [showPhoneConfirmation, setShowPhoneConfirmation] = useState(false);
  const [isOtpSending, setIsOtpSending] = useState(false);
  const [phoneResendTimer, setPhoneResendTimer] = useState(0);
  const phoneResendIntervalRef = useRef(null);

  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [snackbarMessages, setSnackbarMessages] = useState([]);
  const [snackbarType, setSnackbarType] = useState("error");

  const [urlParams, setUrlParams] = useState({
    resumeId: null,
    jobDescriptionId: null,
  });

  // Turnstile state
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [consentGiven, setConsentGiven] = useState(false);
  const [captchaMessage, setCaptchaMessage] = useState(
    "This platform uses a security service to protect against malicious bots. We are verifying you are not a bot.",
  );
  const handleOpenPrivacy = () => {
    window.open("/privacy-policy.html", "_blank");
  };

  // ── Language typeahead ──
  const [language, setLanguage] = useState("");
  const [languageSuggestions, setLanguageSuggestions] = useState([]);
  const [showLanguageSuggestions, setShowLanguageSuggestions] = useState(false);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // ── Location typeahead + DnD ──
  const [location, setLocation] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [selectedLocationSuggestionIndex, setSelectedLocationSuggestionIndex] =
    useState(-1);
  const [activeId, setActiveId] = useState(null);

  // ── Display values ──
  const [experienceDisplay, setExperienceDisplay] = useState("");
  const [expectedCTCDisplay, setExpectedCTCDisplay] = useState("");

  const companyId = useMemo(
    () => sessionStorage.getItem("companyId") || "",
    [],
  );
  const phoneInputWrapperRef = useRef(null);
  const resumeId = sessionStorage.getItem("userId");

  // ── Phone input dropdown: reposition using fixed coords so it escapes overflow clipping ──
  useEffect(() => {
    const wrapper = phoneInputWrapperRef.current;
    if (!wrapper) return;

    const repositionDropdown = () => {
      const btn = wrapper.querySelector(".flag-dropdown");
      const list = wrapper.querySelector(".country-list");
      if (!btn || !list) return;
      requestAnimationFrame(() => {
        const rect = btn.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = Math.min(200, list.scrollHeight || 200);
        const spaceBelow = viewportHeight - rect.bottom;
        const openDown = spaceBelow >= dropdownHeight || spaceBelow >= rect.top;
        list.style.left = rect.left + "px";
        list.style.width = Math.max(rect.width, 260) + "px";
        list.style.bottom = "auto";
        list.style.top = openDown
          ? rect.bottom + 2 + "px"
          : Math.max(8, rect.top - dropdownHeight - 2) + "px";
      });
    };

    // Keep dropdown anchored while the user scrolls or resizes
    const handleScrollOrResize = () => {
      const list = wrapper.querySelector(".country-list");
      if (list) repositionDropdown();
    };

    const observer = new MutationObserver(() => {
      const list = wrapper.querySelector(".country-list");
      if (list) {
        repositionDropdown();
        // Attach listeners when dropdown opens, remove when it closes
        window.addEventListener("scroll", handleScrollOrResize, true);
        window.addEventListener("resize", handleScrollOrResize);
      } else {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      }
    });

    observer.observe(wrapper, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, []);
  const handleOpenTerms = () => {
    window.open("/terms-of-use.html", "_blank");
  };
  // ── Value updaters ──────────────────────────
  const updateFieldValue = (fieldId, value) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
    // Clear error on change
    if (errors[fieldId]) {
      setErrors((prev) => ({ ...prev, [fieldId]: null }));
    }
  };

  const handleCurrencyChange = useCallback(
    (sourceFieldId, newCurrencyCode) => {
      setCurrencyMasterFieldId(sourceFieldId);
      setFormValues((prev) => {
        const next = { ...prev };
        sortedFields.forEach((f) => {
          if (f.type === "currency") {
            const currentVal =
              next[f.id] && typeof next[f.id] === "object"
                ? next[f.id]
                : { amount: "" };
            next[f.id] = {
              ...currentVal,
              currencyCode: newCurrencyCode,
            };
          }
        });
        return next;
      });
      // Clear errors on currency fields if present
      setErrors((prev) => {
        const nextErr = { ...prev };
        sortedFields.forEach((f) => {
          if (f.type === "currency" && nextErr[f.id]) {
            nextErr[f.id] = null;
          }
        });
        return nextErr;
      });
    },
    [sortedFields],
  );

  const addChipValue = (fieldId) => {
    const inputValue = inputValues[fieldId]?.trim();
    if (!inputValue) return;
    if (!formValues[fieldId].includes(inputValue)) {
      updateFieldValue(fieldId, [...formValues[fieldId], inputValue]);
    }
    setInputValues((prev) => ({ ...prev, [fieldId]: "" }));
  };

  const removeChipValue = (fieldId, value) => {
    updateFieldValue(
      fieldId,
      formValues[fieldId].filter((v) => v !== value),
    );
  };
  // Re-initialize form values whenever fields are (re)loaded from session
  useEffect(() => {
    if (normalizedFields.length > 0) {
      setFormValues(buildInitialValues(normalizedFields));
    }
  }, [normalizedFields]);

  // If sessionStorage wasn't ready on first render (e.g. OTP page wrote it
  // just before navigating here), reload fields on mount as a safety net.
  useEffect(() => {
    if (normalizedFields.length === 0) {
      const fields = loadNormalizedFieldsFromSession();
      if (fields.length > 0) setNormalizedFields(fields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   authService.initialize();
  //   if (!authService.isAuthenticated()) {
  //     navigate("/screenup-login");
  //     return;
  //   }
  //   const resumeId = authService.getResumeId();
  //   const jobDescriptionId = sessionStorage.getItem("jdId");
  //   const userIdFromToken = sessionStorage.getItem("userId");

  //   if (!resumeId || !jobDescriptionId) {
  //     console.error("Missing required parameters in JWT token");
  //     return;
  //   }
  //   setUrlParams({ resumeId, jobDescriptionId });
  // }, [navigate]);

  const showSnackbar = useCallback((messages, type = "error") => {
    setSnackbarMessages(Array.isArray(messages) ? messages : [messages]);
    setSnackbarType(type);
  }, []);

  const handleCloseSnackbar = useCallback(() => setSnackbarMessages([]), []);

  const showErrorsInSnackbar = useCallback(
    (errors) => {
      const msgs = Object.keys(errors)
        .filter((f) => errors[f])
        .map((f) => errors[f]);
      if (msgs.length) showSnackbar(msgs, "error");
    },
    [showSnackbar],
  );
  const dataReviewRequired = sessionStorage.getItem("is_review_required");
  const handleSubmit = useCallback(async () => {
    if (isSubmitting || showLoader) return;

    // Validate all required fields
    const newErrors = {};
    sortedFields.forEach((field) => {
      const error = validateField(field, formValues[field.id]);
      if (error) newErrors[field.id] = error;
    });

    if (!consentGiven) {
      newErrors._consent =
        "By submitting this form, you must consent to storing your personal details.";
    }

    if (!turnstileToken && !isCaptchaDisabled) {
      newErrors._captcha = "Please complete the security verification.";
    }

    setErrors(newErrors);

    const fieldErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([k]) => k !== "_captcha"),
    );

    if (Object.keys(newErrors).length > 0) {
      const firstErrorId = Object.keys(newErrors)[0];
      const el = document.getElementById(`field-${firstErrorId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      showErrorsInSnackbar(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    setShowLoader(true);

    try {
      // ── Build dynamic answers array from every rendered field ─────────────
      // Each field carries numericId (question_id / default_id) set during
      // normalizeField(). The answer value is serialised per field type.

      const answers = sortedFields
        .filter((field) => field.numericId != null) // skip fields without a server id
        .map((field) => {
          const raw = formValues[field.id];

          // Serialise value to what the API expects in input.answer
          let answer;
          switch (field.type) {
            // ── number → int ────────────────────────────────────────────────
            case "number":
              answer = parseInt(raw, 10) || 0;
              break;

            // ── text / textarea / phone / listOfValuesText → string ─────────
            case "textarea":
            case "phone":
            case "listOfValuesText":
            case "multipleOptionsSingleSelection":
              answer = raw ?? "";
              break;

            // ── text_multiple_entries → string[] ────────────────────────────
            case "textOnlyMultipleEntries":
            case "multiChipInput":
              answer = Array.isArray(raw) ? raw : [];
              break;

            // ── multiple_option_multi_selection → string[] ──────────────────
            case "multipleOptionsMultipleSelections":
              answer = Array.isArray(raw) ? raw : [];
              break;

            // ── list_of_values_numbers → int ────────────────────────────────
            case "listOfValuesNumbers":
              answer = parseInt(raw, 10) || 0;
              break;

            // ── country / state lists → string[] ───────────────────────────
            case "countryList":
            case "usStateList":
            case "indianStateList":
            case "canadianStateList":
            case "australianStateList":
              answer = raw || "";
              break;
            case "sortableChipInput":
              answer = Array.isArray(raw) ? raw : [];
              break;

            // ── single_date → "YYYY-MM-DD" string ──────────────────────────
            // DOB specifically: calculate age and send as int
            case "singleDate":
            case "date":
              if (field.id === "dob" && raw) {
                const [y, m, d] = raw.split("-");
                answer = `${d}-${m}-${y}`; // "dd-mm-yyyy"
              } else {
                answer = raw || null; // "YYYY-MM-DD" string
              }
              break;

            // ── multiple_dates → string[] ───────────────────────────────────
            case "multipleDates":
              answer = Array.isArray(raw) ? raw : [];
              break;

            // ── date_range → { start_date, end_date } ───────────────────────
            case "dateRange":
              answer = {
                start_date: raw?.from || null,
                end_date: raw?.to || null,
              };
              break;

            // ── currency → { currency, value } ──────────────────────────────
            case "currency":
              answer = {
                currency: raw?.currencyCode || "INR",
                value: parseInt(raw?.amount, 10) || 0,
              };
              break;

            // ── boolean / checkbox ───────────────────────────────────────────
            case "checkbox":
              answer = Boolean(raw);
              break;

            // ── fallback ─────────────────────────────────────────────────────
            default:
              answer = raw ?? "";
          }

          return {
            question_id: field.numericId,
            default_id: field.default_id, // mirrors question_id per API contract
            input: { answer },
          };
        });
      const resumeId = sessionStorage.getItem("userId");
      const jobDescriptionId = sessionStorage.getItem("jdId");

      const response = await apiService.processScreeningInterview(
        resumeId,
        jobDescriptionId,
        answers,
        true,
      );

      setTimeout(() => {
        setIsSubmitting(false);
        setShowLoader(false);

        // response is already the `results` object (normalised in apiService)
        // Fields: resume_id, job_description_id, interview_id, last_date_used,
        //         time_duration, candidate_name, email_sent_to, company_name,
        //         role, timezone
        //const lastDate = response.last_date_used || null;
        const lastDate = sessionStorage.getItem("last_date_used");
        const isExpired = lastDate
          ? new Date(lastDate).setHours(23, 59, 59, 999) < Date.now()
          : false;

        if (
          dataReviewRequired === true ||
          dataReviewRequired === "true" ||
          isExpired
        ) {
          navigate("/welcome-response", {
            state: {
              resumeId: response.resume_id || urlParams.resumeId,
              jobDescriptionId:
                response.job_description_id || urlParams.jobDescriptionId,
              lastDate,
              candidateName: sessionStorage.getItem("candidate_name") || null,
              emailSentTo: response.email_sent_to || null,
              interviewId: response.interview_id || null,
              companyName: response.company_name || null,
            },
          });
        } else {
          navigate("/slot-booking", {
            state: {
              jobTitle: response.role,
              companyName: response.company_name,
              lastDate,
              candidateName: sessionStorage.getItem("candidate_name") || "",
              resumeId: response.resume_id || urlParams.resumeId,
              jobDescriptionId:
                response.job_description_id || urlParams.jobDescriptionId,
              interviewId: response.interview_id,
              timeDuration: response.time_duration || response.time_restriction,
              timezone: response.timezone || "Asia/Calcutta",
              isAssessment: false,
            },
          });
        }
      }, 500);
    } catch (err) {
      const message = err.message || "Something went wrong. Please try again.";
      showSnackbar(message, "error");
      setIsSubmitting(false);
      setShowLoader(false);
    }
  }, [
    isSubmitting,
    showLoader,
    sortedFields,
    formValues,
    phoneNumber,
    urlParams,
    navigate,
    consentGiven,
    turnstileToken,
    isCaptchaDisabled,
    showSnackbar,
    showErrorsInSnackbar,
  ]);
  // For sortable chips — drag-to-reorder
  const moveChip = (fieldId, fromIndex, toIndex) => {
    const chips = [...formValues[fieldId]];
    const [moved] = chips.splice(fromIndex, 1);
    chips.splice(toIndex, 0, moved);
    updateFieldValue(fieldId, chips);
  };
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };
  const debouncedLanguage = useDebounce(language, 300);
  // ── Language API ──
  const fetchLanguageSuggestions = useCallback(
    async (query) => {
      if (!query.trim()) {
        setLanguageSuggestions([]);
        setShowLanguageSuggestions(false);
        return;
      }
      setIsLoadingLanguages(true);
      try {
        const res = await apiClient.get("/languages/search", {
          params: { q: query, limit: 10, company_id: companyId },
        });
        const suggestions = res.data.results || res.data || [];
        setLanguageSuggestions(suggestions);
        setShowLanguageSuggestions(suggestions.length > 0);
      } catch {
        setLanguageSuggestions([]);
        setShowLanguageSuggestions(false);
      } finally {
        setIsLoadingLanguages(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    if (otpVerified) fetchLanguageSuggestions(debouncedLanguage);
  }, [debouncedLanguage, otpVerified, fetchLanguageSuggestions]);

  const handleInputChange = useCallback((e) => {
    setLanguage(formatText(e.target.value));
    setSelectedSuggestionIndex(-1);
  }, []);

  const handleSuggestionClick = useCallback(
    (suggestion) => {
      const langField = sortedFields.find((f) => f.id === "language");
      const fieldId = langField?.id || "language";
      const current = formValues[fieldId] || [];
      if (!current.includes(suggestion))
        updateFieldValue(fieldId, [...current, suggestion]);
      setLanguage("");
      setShowLanguageSuggestions(false);
      setSelectedSuggestionIndex(-1);
    },
    [formValues, sortedFields, updateFieldValue],
  );

  const handleRemoveLanguage = useCallback(
    (lang) => {
      const langField = sortedFields.find((f) => f.id === "language");
      const fieldId = langField?.id || "language";
      updateFieldValue(
        fieldId,
        (formValues[fieldId] || []).filter((l) => l !== lang),
      );
    },
    [formValues, sortedFields, updateFieldValue],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestionIndex((p) =>
          p < languageSuggestions.length - 1 ? p + 1 : p,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestionIndex((p) => (p > 0 ? p - 1 : -1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const toAdd =
          selectedSuggestionIndex >= 0
            ? languageSuggestions[selectedSuggestionIndex]
            : language.trim();
        const langField = sortedFields.find((f) => f.id === "language");
        const fieldId = langField?.id || "language";
        const current = formValues[fieldId] || [];
        if (toAdd && !current.includes(toAdd))
          updateFieldValue(fieldId, [...current, toAdd]);
        setLanguage("");
        setShowLanguageSuggestions(false);
        setSelectedSuggestionIndex(-1);
      } else if (e.key === "Escape") {
        setShowLanguageSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    },
    [
      language,
      languageSuggestions,
      selectedSuggestionIndex,
      formValues,
      updateFieldValue,
    ],
  );

  const languageSuggestionMouseDown = useRef(false);
  const locationSuggestionMouseDown = useRef(false);

  const handleInputBlur = useCallback(() => {
    if (languageSuggestionMouseDown.current) return;
    setTimeout(() => {
      setShowLanguageSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }, 200);
  }, []);
  const handleInputFocus = useCallback(() => {
    if (language.trim() && languageSuggestions.length > 0)
      setShowLanguageSuggestions(true);
  }, [language, languageSuggestions]);

  // ── Location API ──
  const fetchLocationSuggestions = useCallback(
    async (query) => {
      if (!query.trim()) {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
        return;
      }
      setIsLoadingLocations(true);
      try {
        const res = await apiClient.get("/locations/search", {
          params: { q: query, limit: 10, company_id: companyId },
        });
        const suggestions = res?.data?.results || [];
        setLocationSuggestions(suggestions);
        setShowLocationSuggestions(suggestions.length > 0);
      } catch {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
      } finally {
        setIsLoadingLocations(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    fetchLocationSuggestions(location);
  }, [location, fetchLocationSuggestions]);

  const handleLocationSuggestionClick = useCallback(
    (suggestion) => {
      const locField = sortedFields.find((f) => f.id === "preferred_locations");
      const fieldId = locField?.id || "preferred_locations";
      const current = formValues[fieldId] || [];
      if (!current.includes(suggestion))
        updateFieldValue(fieldId, [...current, suggestion]);
      setLocation("");
      setShowLocationSuggestions(false);
      setSelectedLocationSuggestionIndex(-1);
    },
    [formValues, sortedFields, updateFieldValue],
  );

  const handleLocationKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedLocationSuggestionIndex((p) =>
          p < locationSuggestions.length - 1 ? p + 1 : p,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedLocationSuggestionIndex((p) => (p > 0 ? p - 1 : -1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const toAdd =
          selectedLocationSuggestionIndex >= 0
            ? locationSuggestions[selectedLocationSuggestionIndex]
            : location.trim();
        const locField = sortedFields.find(
          (f) => f.id === "preferred_locations",
        );
        const fieldId = locField?.id || "preferred_locations";
        const current = formValues[fieldId] || [];
        if (toAdd && !current.includes(toAdd))
          updateFieldValue(fieldId, [...current, toAdd]);
        setLocation("");
        setShowLocationSuggestions(false);
        setSelectedLocationSuggestionIndex(-1);
      } else if (e.key === "Escape") {
        setShowLocationSuggestions(false);
        setSelectedLocationSuggestionIndex(-1);
      }
    },
    [
      location,
      locationSuggestions,
      selectedLocationSuggestionIndex,
      formValues,
      updateFieldValue,
    ],
  );

  const handleRemoveLocation = useCallback(
    (loc) => {
      const locField = sortedFields.find((f) => f.id === "preferred_locations");
      const fieldId = locField?.id || "preferred_locations";
      updateFieldValue(
        fieldId,
        (formValues[fieldId] || []).filter((l) => l !== loc),
      );
    },
    [formValues, sortedFields, updateFieldValue],
  );

  // ── DnD ──
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: () => {} }),
  );

  const handleDragStart = useCallback(
    (event) => setActiveId(event.active.id),
    [],
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        const locField = sortedFields.find(
          (f) => f.id === "preferred_locations",
        );
        const fieldId = locField?.id || "preferred_locations";
        const locs = formValues[fieldId] || [];
        const oldIndex = locs.indexOf(active.id);
        const newIndex = locs.indexOf(over.id);
        updateFieldValue(fieldId, arrayMove(locs, oldIndex, newIndex));
      }
      setActiveId(null);
    },
    [formValues, sortedFields, updateFieldValue],
  );

  // ── CTC ──
  const handleExpectedCTCChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (/^\d*$/.test(value.replace(/,/g, ""))) {
        const num = value.replace(/,/g, "");
        const formatted = num ? num.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
        setExpectedCTCDisplay(formatted);
        updateFieldValue("expected_ctc", {
          ...formValues["expected_ctc"],
          amount: num,
        });
      }
    },
    [formValues, updateFieldValue],
  );

  const startPhoneResendTimer = useCallback(() => {
    setPhoneResendTimer(30);
    if (phoneResendIntervalRef.current)
      clearInterval(phoneResendIntervalRef.current);
    phoneResendIntervalRef.current = setInterval(() => {
      setPhoneResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(phoneResendIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const PhoneConfirmationModal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4">
      <div className="bg-white rounded-md shadow-xl w-[300px] md:w-[460px] max-w-full overflow-hidden">
        {/* Header bar — matches camera/mic/speaker confirmation modals */}
        <div className="flex items-center justify-between px-4 py-2 bg-[#E7E9EB] border-b border-[#CCCCCC]">
          <p className="font-medium text-sm text-[#111729]">Confirming...</p>
          <button
            onClick={() => setShowPhoneConfirmation(false)}
            className="text-gray-500"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col items-center text-[#111729]">
          <p className="text-sm mb-2 text-center">
            Are you sure you have entered the correct Phone Number?
          </p>
          <p className="font-semibold mb-6 text-center">+{phoneNumber}</p>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowPhoneConfirmation(false)}
              className="px-8 py-2 border rounded-md flex items-center space-x-1 text-sm"
              style={{ border: "1px solid #002147" }}
            >
              <img src={removeDarkImg} alt="No" className="w-3 h-3 mr-2" />
              <span>No</span>
            </button>
            <button
              onClick={async () => {
                setShowPhoneConfirmation(false);
                setIsOtpSending(true);
                try {
                  await apiService.sendOtp(
                    sessionStorage.getItem("userId"),
                    phoneNumber,
                  );
                  showSnackbar(
                    "OTP sent successfully to your phone number",
                    "success",
                  );
                  setOtpStep("otp");
                  startPhoneResendTimer();
                } catch (error) {
                  showSnackbar(error.message || "Failed to send OTP", "error");
                } finally {
                  setIsOtpSending(false);
                }
              }}
              className="px-8 py-2 bg-[#111729] text-white rounded-md flex items-center space-x-1 text-sm"
            >
              <img src={rightImg} alt="Yes" className="w-4 h-4 mr-2" />
              <span>Yes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  // ─────────────────────────────────────────────
  // Field renderers — one case per supported type
  // ─────────────────────────────────────────────
  const renderField = (field) => {
    const value = formValues[field.id];

    // ── List-backed dropdowns (countries, states…) ──
    if (LIST_TYPES.includes(field.type)) {
      const options = FIELD_OPTIONS[field.type] || [];
      return (
        <select
          value={value}
          onChange={(e) => updateFieldValue(field.id, e.target.value)}
          className="border p-2 rounded w-full max-w-[450px]"
        >
          <option value="">Select</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    switch (field.type) {
      // ── Text ──
      case "text":
      case "textOnly":
      case "textIncludingNumbers":
        return (
          <input
            type="text"
            placeholder={field.placeholder || ""}
            value={value}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder-gray-500 px-2 py-[3.5px] w-full max-w-[450px] placeholder:text-[#CCCCCC]"
            style={{ borderWidth: "0.25px" }}
          />
        );
        return (
          <input
            type="text"
            placeholder={field.placeholder || ""}
            value={value}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="border p-2 rounded w-full max-w-[450px] placeholder:text-[#CCCCCC]"
          />
        );

      // ── Number ──
      case "number":
        return (
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder={field.placeholder || ""}
              value={field.id === "experience" ? experienceDisplay : value}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d*\.?\d*$/.test(v.replace(/,/g, ""))) {
                  if (field.id === "experience") setExperienceDisplay(v);
                  updateFieldValue(field.id, v.replace(/,/g, ""));
                }
              }}
              className="bg-[#F9FAFB] border border-[#666666] focus:outline-none text-allfonts px-2 py-[3.5px] placeholder:text-[#CCCCCC] width-450 "
              style={{ borderRadius: "4px", borderWidth: "0.25px" }}
            />
            {field.suffix && (
              <span className="text-allfonts text-fontColor whitespace-nowrap">
                {field.suffix}
              </span>
            )}
          </div>
        );

      // ── List of Values Numbers (multi-select, same UI as multipleOptionsMultipleSelections) ──
      case "listOfValuesNumbers":
        {
          const selected = Array.isArray(value) ? value : [];
          const searchKey = `__lvn_search_${field.id}`;
          const isOpenKey = `__lvn_open_${field.id}`;
          const searchTerm = (inputValues[searchKey] || "").toLowerCase();
          const isOpen = !!inputValues[isOpenKey];
          const allOptions = field.options || [];
          const filtered = searchTerm
            ? allOptions.filter((o) =>
                String(o).toLowerCase().includes(searchTerm),
              )
            : allOptions;

          const toggleOption = (option) => {
            const next = selected.includes(option)
              ? selected.filter((o) => o !== option)
              : [...selected, option];
            updateFieldValue(field.id, next);
          };

          const removeChip = (option) => {
            updateFieldValue(
              field.id,
              selected.filter((o) => o !== option),
            );
          };

          return (
            <div className="flex flex-col gap-2" style={{ maxWidth: "450px" }}>
              {/* ── Trigger / search input ── */}
              <div className="relative">
                <div
                  className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] px-2 py-[3.5px] flex items-center gap-2 cursor-text w-full max-w-[450px]"
                  style={{ borderWidth: "0.25px", minHeight: "32px" }}
                  onClick={() =>
                    setInputValues((prev) => ({
                      ...prev,
                      [isOpenKey]: !prev[isOpenKey],
                      [searchKey]: "",
                    }))
                  }
                >
                  <input
                    type="text"
                    placeholder={
                      selected.length === 0
                        ? "Search and select…"
                        : `${selected.length} selected — search to add more`
                    }
                    value={inputValues[searchKey] || ""}
                    onChange={(e) => {
                      e.stopPropagation();
                      setInputValues((prev) => ({
                        ...prev,
                        [searchKey]: e.target.value,
                        [isOpenKey]: true,
                      }));
                    }}
                    onFocus={() =>
                      setInputValues((prev) => ({ ...prev, [isOpenKey]: true }))
                    }
                    onBlur={() =>
                      setTimeout(
                        () =>
                          setInputValues((prev) => ({
                            ...prev,
                            [isOpenKey]: false,
                            [searchKey]: "",
                          })),
                        200,
                      )
                    }
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 bg-transparent focus:outline-none text-allfonts placeholder:text-[#CCCCCC] min-w-0"
                    autoComplete="off"
                  />
                  {/* chevron */}
                  <svg
                    className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                {/* ── Dropdown list ── */}
                {isOpen && (
                  <div
                    className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-52 overflow-y-auto mt-0.5"
                    style={{ borderWidth: "0.25px" }}
                  >
                    {filtered.length === 0 ? (
                      <div className="px-3 py-2 text-allfonts text-gray-400 italic">
                        No options found
                      </div>
                    ) : (
                      filtered.map((option) => {
                        const isChecked = selected.includes(option);
                        return (
                          <div
                            key={option}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              toggleOption(option);
                            }}
                            className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-allfonts hover:bg-gray-50 ${
                              isChecked ? "bg-[#f0f3ff]" : ""
                            }`}
                          >
                            {/* checkbox indicator */}
                            <div
                              className={`w-4 h-4 shrink-0 rounded-sm border flex items-center justify-center ${
                                isChecked
                                  ? "bg-[#002147] border-[#002147]"
                                  : "border-gray-400 bg-white"
                              }`}
                            >
                              {isChecked && (
                                <svg
                                  className="w-3 h-3 text-white"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                >
                                  <path
                                    d="M2 6l3 3 5-5"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>
                            <span
                              className={
                                isChecked
                                  ? "font-medium text-[#002147]"
                                  : "text-fontColor"
                              }
                            >
                              {option}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* ── Selected chips box ── */}
              {selected.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {selected.map((item) => (
                    <div
                      key={item}
                      className="bg-gray-100 border border-[#CCCCCC] px-3 py-1 rounded-full flex items-center gap-2 text-allfonts"
                      style={{ borderWidth: "0.25px" }}
                    >
                      <span>{item}</span>
                      <button
                        type="button"
                        onClick={() => removeChip(item)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder={field.placeholder || ""}
              value={value}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              className="border p-2 rounded w-[200px] placeholder:text-[#CCCCCC]"
            />
            {field.suffix && (
              <span className="text-gray-500 text-sm">{field.suffix}</span>
            )}
          </div>
        );

      // ── Phone ──
      case "phone":
        return (
          <PhoneInput
            country={"in"}
            value={value}
            onChange={(val) => updateFieldValue(field.id, val)}
            inputStyle={{ width: "100%", maxWidth: "450px" }}
          />
        );

      // ── Single Date ──
      case "date":
      case "singleDate": {
        const today = new Date();
        const todayStr = today.toISOString().split("T")[0];

        // Detect whether min/max are "today", a YYYY-MM-DD string, or a numeric age offset
        const isDateLikeSingle = (v) =>
          v === "today" ||
          (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v));

        let minDate = null;
        let maxDate = null;

        if (isDateLikeSingle(field.min) || isDateLikeSingle(field.max)) {
          // Direct date bounds (e.g. "When are you planning to leave")
          minDate =
            field.min === "today"
              ? todayStr
              : field.min != null
                ? field.min
                : null;
          maxDate =
            field.max === "today"
              ? todayStr
              : field.max != null
                ? field.max
                : null;
        } else {
          // Numeric age offsets → invert for HTML date input
          // field.min = min age (e.g. 18) → HTML max (can't pick too recent)
          // field.max = max age (e.g. 90) → HTML min (can't pick too far back)
          maxDate =
            field.min != null
              ? new Date(
                  today.getFullYear() - field.min,
                  today.getMonth(),
                  today.getDate(),
                )
                  .toISOString()
                  .split("T")[0]
              : null;
          minDate =
            field.max != null
              ? new Date(
                  today.getFullYear() - field.max,
                  today.getMonth(),
                  today.getDate(),
                )
                  .toISOString()
                  .split("T")[0]
              : null;
        }

        return (
          <CustomDatePicker
            name={field.id}
            value={value}
            min={minDate}
            max={maxDate}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
          />
        );
      }

      // ── Multiple Dates ──
      case "multipleDates": {
        const dates = Array.isArray(value) ? value : [];
        return (
          <div>
            <CustomDatePicker
              name={field.id}
              value=""
              min={field.min}
              max={field.max}
              onChange={(e) => {
                const picked = e.target.value;

                if (picked && !dates.includes(picked)) {
                  updateFieldValue(field.id, [...dates, picked]);
                }
              }}
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {dates.map((d) => (
                <div
                  key={d}
                  className="bg-gray-100 border border-[#CCCCCC] px-3 py-1 rounded-full flex items-center gap-2 text-allfonts"
                  style={{ borderWidth: "0.25px" }}
                >
                  <span>{d}</span>
                  <button
                    type="button"
                    onClick={() =>
                      updateFieldValue(
                        field.id,
                        dates.filter((x) => x !== d),
                      )
                    }
                    className="text-gray-400 hover:text-red-500"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // ── Date Range ──
      case "dateRange": {
        const range =
          value && typeof value === "object" ? value : { from: "", to: "" };
        return (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex flex-col gap-1 w-full sm:w-auto">
              <label className="text-allfonts-small text-gray-500">From</label>

              <CustomDatePicker
                name={`${field.id}_from`}
                value={range.from}
                min={field.min}
                max={field.max}
                onChange={(e) =>
                  updateFieldValue(field.id, {
                    ...range,
                    from: e.target.value,
                  })
                }
              />
            </div>
            <span className="hidden sm:inline mt-5 text-gray-400">→</span>
            <div className="flex flex-col gap-1 w-full sm:w-auto">
              <label className="text-allfonts-small text-gray-500">To</label>

              <CustomDatePicker
                name={`${field.id}_to`}
                value={range.to}
                min={range.from || field.min}
                max={field.max}
                onChange={(e) =>
                  updateFieldValue(field.id, {
                    ...range,
                    to: e.target.value,
                  })
                }
              />
            </div>
          </div>
        );
      }

      // ── Dropdown (admin-defined options) ──
      case "dropdown":
      case "listOfValuesText":
      case "countryList":
      case "indianStateList":
      case "usStateList":
      case "canadianStateList":
      case "australianStateList":
        {
          const options = LIST_TYPES.includes(field.type)
            ? FIELD_OPTIONS[field.type] || []
            : field.options || [];
          // For large lists (countries / states) render a searchable select
          const isLargeList = LIST_TYPES.includes(field.type);
          if (isLargeList) {
            // Each large-list field gets its own local search term via inputValues
            const searchTerm = (
              inputValues[`__search_${field.id}`] || ""
            ).toLowerCase();
            const filtered = searchTerm
              ? options.filter((o) => o.toLowerCase().includes(searchTerm))
              : options;
            return (
              <div
                className="flex flex-col gap-1"
                style={{ maxWidth: "450px" }}
              >
                <input
                  type="text"
                  placeholder={`Search ${field.placeholder || "…"}`}
                  value={inputValues[`__search_${field.id}`] || ""}
                  onChange={(e) =>
                    setInputValues((prev) => ({
                      ...prev,
                      [`__search_${field.id}`]: e.target.value,
                    }))
                  }
                  className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] px-2 py-[3.5px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] w-full"
                  style={{ borderWidth: "0.25px" }}
                />
                <div className="relative">
                  <select
                    value={value}
                    onChange={(e) => updateFieldValue(field.id, e.target.value)}
                    size={6}
                    className={`bg-[#F9FAFB] w-full border border-[#666666] rounded-[4px] px-2 py-[3.5px] focus:outline-none text-allfonts ${
                      value ? "text-fontColor" : "text-[#CCCCCC]"
                    }`}
                    style={{ borderWidth: "0.25px" }}
                  >
                    <option value="" disabled>
                      {field.placeholder || "Select…"}
                    </option>
                    {filtered.map((opt) => (
                      <option key={opt} value={opt} className="text-black py-1">
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                {value && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-allfonts-small text-fontColor">
                      Selected:
                    </span>
                    <span className="bg-[#002147] text-white text-allfonts-small px-2 py-0.5 rounded-full">
                      {value}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateFieldValue(field.id, "")}
                      className="text-allfonts-small text-gray-400 hover:text-red-500 underline"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            );
          }
          return (
            <div className="relative mt-2" style={{ maxWidth: "450px" }}>
              <select
                value={value}
                onChange={(e) => updateFieldValue(field.id, e.target.value)}
                className={`bg-[#F9FAFB] w-full max-w-[450px] border border-[#666666] rounded-[4px] px-2 py-[3.5px] focus:outline-none text-allfonts appearance-none pr-10 ${
                  value ? "text-fontColor" : "text-[#CCCCCC]"
                }`}
                style={{ borderWidth: "0.25px" }}
              >
                <option value="" disabled>
                  {field.placeholder || "Select an option"}
                </option>
                {options.map((opt) => (
                  <option key={opt} value={opt} className="text-black">
                    {opt}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                <svg
                  className="w-4 h-4 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          );
        }
        return (
          <select
            value={value}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="border p-2 rounded w-full max-w-[450px]"
          >
            <option value="">Select</option>
            {(field.options || []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "radio":
      case "multipleOptionsSingleSelection":
        return (
          <div className="flex flex-wrap gap-6 mt-2">
            {(field.options || []).map((option) => (
              <label
                key={option}
                className="flex items-center space-x-2 text-allfonts cursor-pointer text-fontColor"
              >
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => updateFieldValue(field.id, e.target.value)}
                  className="w-5 h-5 "
                  style={{ accentColor: "#002147" }}
                />
                <span className="capitalize">{option}</span>
              </label>
            ))}
          </div>
        );
        return (
          <div className="flex gap-4 flex-wrap">
            {(field.options || []).map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => updateFieldValue(field.id, e.target.value)}
                  style={{ accentColor: "#002147" }}
                />
                {option}
              </label>
            ))}
          </div>
        );

      // ── Checkbox ──
      case "checkbox":
        return (
          <label className="flex items-center space-x-2 text-allfonts cursor-pointer text-fontColor">
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => updateFieldValue(field.id, e.target.checked)}
              className="transform scale-125"
              style={{ accentColor: "#002147" }}
            />
            <span>{field.label}</span>
          </label>
        );
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => updateFieldValue(field.id, e.target.checked)}
              style={{ accentColor: "#002147" }}
            />
            <span>{field.label}</span>
          </label>
        );

      // ── Currency ──
      case "currency": {
        const currencyFields = sortedFields.filter(
          (f) => f.type === "currency",
        );
        const hasMultipleCurrencyFields = currencyFields.length > 1;

        // Determine master field: explicitly set, or the first currency field that has a currency selected
        const anyCurrencySelected = currencyFields.some(
          (f) => formValues[f.id]?.currencyCode,
        );
        const effectiveMasterId =
          currencyMasterFieldId ||
          (anyCurrencySelected ? currencyFields[0]?.id : null);

        const isLocked =
          hasMultipleCurrencyFields &&
          effectiveMasterId !== null &&
          field.id !== effectiveMasterId;

        const curr =
          value && typeof value === "object"
            ? value
            : { amount: "", currencyCode: "" };

        // If locked, ensure currencyCode matches the master field's currency
        const activeCurrencyCode =
          isLocked && effectiveMasterId
            ? formValues[effectiveMasterId]?.currencyCode ||
              curr.currencyCode ||
              ""
            : curr.currencyCode || "";

        // Format the stored raw number for display
        const displayAmount = curr.amount
          ? curr.amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          : "";

        return (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
            <div className="relative w-full max-w-[260px] md:max-w-[180px]">
              <select
                style={{ borderWidth: "0.25px", borderRadius: "4px" }}
                disabled={isLocked}
                title={
                  isLocked
                    ? "Locked to selected currency across all fields"
                    : undefined
                }
                className={`w-full appearance-none px-2 pr-8 py-[3.5px] focus:outline-none text-allfonts ${
                  isLocked
                    ? "bg-[#E5E7EB] text-gray-500 cursor-not-allowed border-[#CCCCCC]"
                    : `bg-[#F9FAFB] border border-[#666666] ${
                        activeCurrencyCode ? "text-fontColor" : "text-[#CCCCCC]"
                      }`
                }`}
                value={activeCurrencyCode}
                onChange={(e) => handleCurrencyChange(field.id, e.target.value)}
              >
                <option value="" disabled>
                  Currency
                </option>

                {currenciesOption.map((c) => (
                  <option key={c.code} value={c.code} className="text-black">
                    {c.name} {c.symbol}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                {isLocked ? (
                  <svg
                    className="w-3.5 h-3.5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4 text-gray-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            </div>
            <input
              type="text"
              placeholder="CTC"
              value={displayAmount}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, "");
                if (/^\d*$/.test(raw)) {
                  updateFieldValue(field.id, {
                    ...curr,
                    currencyCode: activeCurrencyCode,
                    amount: raw,
                  });
                }
              }}
              style={{
                borderWidth: "0.25px",
                borderRadius: "4px",
              }}
              className="bg-[#F9FAFB] border border-[#666666] px-2 py-[3.5px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] w-full max-w-[260px]"
            />
            <p className="text-allfonts text-fontColor whitespace-nowrap shrink-0">
              {field.suffix || "Per Annum"}
            </p>
          </div>
        );
      }
      case "ethnicityDropdown":
        return (
          <div className="relative mt-2" style={{ maxWidth: "450px" }}>
            <select
              value={value}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              className={`bg-[#F9FAFB] w-full max-w-[450px] border border-[#666666] rounded-[4px] px-2 py-[3.5px] focus:outline-none text-allfonts appearance-none pr-10 ${
                value ? "text-fontColor" : "text-[#CCCCCC]"
              }`}
              style={{ borderWidth: "0.25px" }}
            >
              <option value="" disabled>
                {field.placeholder || "Select ethnicity"}
              </option>

              {(field.options?.length
                ? field.options
                : ethnicityOptions.map((o) => o.name)
              ).map((opt) => (
                <option key={opt} value={opt} className="text-black">
                  {opt}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              <svg
                className="w-4 h-4 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        );
      // ── Multi Chip Input (unordered tags) ──
      // If this is the language field (semantic id = "language"), use the
      // language typeahead + API suggestions. For any other text_multiple_entries
      // field, fall through to the generic chip input below.
      case "multiChipInput":
      case "textOnlyMultipleEntries":
        if (field.id === "language") {
          return (
            <div style={{ maxWidth: "450px" }}>
              <div className="relative">
                <input
                  type="text"
                  placeholder={field.placeholder || "Start typing…"}
                  value={language}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleInputBlur}
                  onFocus={handleInputFocus}
                  className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] px-2 py-[3.5px] w-full max-w-[450px]"
                  autoComplete="off"
                  style={{ borderWidth: "0.25px" }}
                />
                {isLoadingLanguages && (
                  <div className="absolute right-3 top-1 text-gray-400 text-allfonts">
                    Loading...
                  </div>
                )}
                {showLanguageSuggestions && languageSuggestions.length > 0 && (
                  <div
                    className="absolute top-full left-0 w-full bg-white border text-allfonts border-gray-300 rounded-md shadow-lg z-50 max-h-48 overflow-y-auto mt-0.5"
                    onMouseDown={() => {
                      languageSuggestionMouseDown.current = true;
                    }}
                    onMouseUp={() => {
                      languageSuggestionMouseDown.current = false;
                    }}
                    onTouchStart={() => {
                      languageSuggestionMouseDown.current = true;
                    }}
                    onTouchEnd={() => {
                      languageSuggestionMouseDown.current = false;
                    }}
                  >
                    {languageSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`px-3 py-2 cursor-pointer text-allfonts hover:bg-gray-100 ${index === selectedSuggestionIndex ? "bg-blue-100" : ""}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onPointerDown={(e) => e.preventDefault()}
                        onClick={() => handleSuggestionClick(suggestion)}
                        onMouseEnter={() => setSelectedSuggestionIndex(index)}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div
                className={`flex flex-wrap gap-2 text-allfonts ${Array.isArray(value) && value.length > 0 ? "mt-2" : ""}`}
              >
                {(Array.isArray(value) ? value : []).map((item) => (
                  <CommonChip
                    key={item}
                    unitName={item}
                    onRemove={handleRemoveLanguage}
                    color={false}
                  />
                ))}
              </div>
            </div>
          );
        }
        // Generic text_multiple_entries — plain chip input with Enter-to-add
        return (
          <div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={field.placeholder || "Type and press Enter"}
                value={inputValues[field.id] || ""}
                onChange={(e) =>
                  setInputValues((prev) => ({
                    ...prev,
                    [field.id]: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addChipValue(field.id);
                  }
                }}
                className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] px-2 py-[3.5px] w-full max-w-[450px]"
                style={{ borderWidth: "0.25px" }}
              />
              {/* <button
                type="button"
                onClick={() => addChipValue(field.id)}
                className="border px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-allfonts"
              >
                Add
              </button> */}
            </div>
            <div
              className={`flex flex-wrap gap-2 text-allfonts ${Array.isArray(value) && value.length > 0 ? "mt-2" : ""}`}
            >
              {(Array.isArray(value) ? value : []).map((item) => (
                <CommonChip
                  key={item}
                  unitName={item}
                  onRemove={(v) => removeChipValue(field.id, v)}
                  color={false}
                />
              ))}
            </div>
          </div>
        );
      // ── Sortable Chip Input (ordered, draggable tags) ──
      case "sortableChipInput": {
        const locs = formValues[field.id] || [];
        return (
          <div className="flex flex-col">
            <div style={{ maxWidth: "450px" }}>
              <div className="relative">
                <input
                  type="text"
                  placeholder={field.placeholder || "Location"}
                  value={location}
                  onChange={(e) => setLocation(formatText(e.target.value))}
                  onKeyDown={handleLocationKeyDown}
                  onBlur={() => {
                    if (locationSuggestionMouseDown.current) return;
                    setTimeout(() => {
                      setShowLocationSuggestions(false);
                      setSelectedLocationSuggestionIndex(-1);
                    }, 200);
                  }}
                  onFocus={() => {
                    if (location.trim() && locationSuggestions.length > 0)
                      setShowLocationSuggestions(true);
                  }}
                  className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] px-2 py-[3.5px] w-full max-w-[450px]"
                  autoComplete="off"
                  style={{ borderWidth: "0.25px" }}
                />
                {isLoadingLocations && (
                  <div className="absolute right-3 top-1 text-gray-400 text-allfonts">
                    Loading...
                  </div>
                )}
                {showLocationSuggestions && locationSuggestions.length > 0 && (
                  <div
                    className="absolute top-full left-0 w-full bg-white border text-allfonts border-gray-300 rounded-md shadow-lg z-50 max-h-48 overflow-y-auto mt-0.5"
                    onMouseDown={() => {
                      locationSuggestionMouseDown.current = true;
                    }}
                    onMouseUp={() => {
                      locationSuggestionMouseDown.current = false;
                    }}
                    onTouchStart={() => {
                      locationSuggestionMouseDown.current = true;
                    }}
                    onTouchEnd={() => {
                      locationSuggestionMouseDown.current = false;
                    }}
                  >
                    {locationSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`px-3 py-2 cursor-pointer text-allfonts hover:bg-gray-100 ${index === selectedLocationSuggestionIndex ? "bg-blue-100" : ""}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onPointerDown={(e) => e.preventDefault()}
                        onClick={() =>
                          handleLocationSuggestionClick(suggestion)
                        }
                        onMouseEnter={() =>
                          setSelectedLocationSuggestionIndex(index)
                        }
                      >
                        {formatText(suggestion)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="w-full">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={locs}
                    strategy={verticalListSortingStrategy}
                  >
                    <div
                      className={`flex flex-wrap gap-2 text-allfonts max-h-32 overflow-y-auto custom-scrollbar ${locs.length > 0 ? "mt-2" : ""}`}
                      style={{ backgroundColor: "#fff" }}
                    >
                      {locs.map((loc, index) => (
                        <DraggableChip
                          key={loc}
                          id={loc}
                          index={index}
                          number={index + 1}
                          label={loc}
                          onRemove={handleRemoveLocation}
                        />
                      ))}
                    </div>
                  </SortableContext>
                  <DragOverlay>
                    {activeId ? (
                      <div className="flex items-center bg-white border border-borderColor rounded-full px-1 py-[4px] shadow-lg opacity-90">
                        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-btnBgColor rounded-full text-white text-tertiaryFontSize mr-2">
                          {locs.indexOf(activeId) + 1}
                        </div>
                        <div className="mr-2 select-none">{activeId}</div>
                        <div className="flex-shrink-0 p-1 rounded-full">
                          <img
                            src={removeImg}
                            alt="remove"
                            className="w-[10px] h-[10px]"
                          />
                        </div>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              </div>
            </div>
          </div>
        );
      }

      // ── Multiple Options Multiple Selections (checkbox group) ──
      case "multipleOptionsMultipleSelections": {
        const selected = Array.isArray(value) ? value : [];
        const searchKey = `__mms_search_${field.id}`;
        const isOpenKey = `__mms_open_${field.id}`;
        const searchTerm = (inputValues[searchKey] || "").toLowerCase();
        const isOpen = !!inputValues[isOpenKey];
        const allOptions = field.options || [];
        const filtered = searchTerm
          ? allOptions.filter((o) => o.toLowerCase().includes(searchTerm))
          : allOptions;

        const toggleOption = (option) => {
          const next = selected.includes(option)
            ? selected.filter((o) => o !== option)
            : [...selected, option];
          updateFieldValue(field.id, next);
        };

        const removeChip = (option) => {
          updateFieldValue(
            field.id,
            selected.filter((o) => o !== option),
          );
        };

        return (
          <div className="flex flex-col gap-2" style={{ maxWidth: "450px" }}>
            {/* ── Trigger / search input ── */}
            <div className="relative">
              <div
                className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] px-2 py-[3.5px] flex items-center gap-2 cursor-text w-full max-w-[450px]"
                style={{ borderWidth: "0.25px", minHeight: "32px" }}
                onClick={() =>
                  setInputValues((prev) => ({
                    ...prev,
                    [isOpenKey]: !prev[isOpenKey],
                    [searchKey]: "",
                  }))
                }
              >
                <input
                  type="text"
                  placeholder={
                    selected.length === 0
                      ? "Search and select…"
                      : `${selected.length} selected — search to add more`
                  }
                  value={inputValues[searchKey] || ""}
                  onChange={(e) => {
                    e.stopPropagation();
                    setInputValues((prev) => ({
                      ...prev,
                      [searchKey]: e.target.value,
                      [isOpenKey]: true,
                    }));
                  }}
                  onFocus={() =>
                    setInputValues((prev) => ({ ...prev, [isOpenKey]: true }))
                  }
                  onBlur={() =>
                    setTimeout(
                      () =>
                        setInputValues((prev) => ({
                          ...prev,
                          [isOpenKey]: false,
                          [searchKey]: "",
                        })),
                      200,
                    )
                  }
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 bg-transparent focus:outline-none text-allfonts placeholder:text-[#CCCCCC] min-w-0"
                  autoComplete="off"
                />
                {/* chevron */}
                <svg
                  className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* ── Dropdown list ── */}
              {isOpen && (
                <div
                  className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-52 overflow-y-auto mt-0.5"
                  style={{ borderWidth: "0.25px" }}
                >
                  {filtered.length === 0 ? (
                    <div className="px-3 py-2 text-allfonts text-gray-400 italic">
                      No options found
                    </div>
                  ) : (
                    filtered.map((option) => {
                      const isChecked = selected.includes(option);
                      return (
                        <div
                          key={option}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            toggleOption(option);
                          }}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-allfonts hover:bg-gray-50 ${
                            isChecked ? "bg-[#f0f3ff]" : ""
                          }`}
                        >
                          {/* checkbox indicator */}
                          <div
                            className={`w-4 h-4 shrink-0 rounded-sm border flex items-center justify-center ${
                              isChecked
                                ? "bg-[#002147] border-[#002147]"
                                : "border-gray-400 bg-white"
                            }`}
                          >
                            {isChecked && (
                              <svg
                                className="w-3 h-3 text-white"
                                viewBox="0 0 12 12"
                                fill="none"
                              >
                                <path
                                  d="M2 6l3 3 5-5"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <span
                            className={
                              isChecked
                                ? "font-medium text-[#002147]"
                                : "text-fontColor"
                            }
                          >
                            {option}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* ── Selected chips box ── */}
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-1">
                {selected.map((item) => (
                  <div
                    key={item}
                    className="bg-gray-100 border border-[#CCCCCC] px-3 py-1 rounded-full flex items-center gap-2 text-allfonts"
                    style={{ borderWidth: "0.25px" }}
                  >
                    <span>{item}</span>
                    <button
                      type="button"
                      onClick={() => removeChip(item)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      // ── Textarea (long text) ──
      case "textarea": {
        return (
          <textarea
            placeholder={field.placeholder || ""}
            value={value}
            rows={4}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] px-2 py-[6px] w-full max-w-[450px] resize-y"
            style={{ borderWidth: "0.25px" }}
          />
        );
      }

      // ── Fallback ──
      default:
        return (
          <div className="text-sm text-red-400 italic">
            Unsupported field type: <code>{field.type}</code>. Add a case in{" "}
            <code>renderField()</code>.
          </div>
        );
    }
  };

  // ─────────────────────────────────────────────
  // Success screen
  // ─────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="h-screen flex flex-col bg-[#F9FAFB] overflow-hidden">
        <div
          className="p-4 px-4 border-b border-[#CCCCCC] bg-[#ffffff]"
          style={{ borderBottomWidth: "0.25px" }}
        >
          <Header />
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div
            className="bg-white border border-[#CCCCCC] rounded-xl p-10 text-center shadow-sm"
            style={{ borderWidth: "0.25px" }}
          >
            <div className="text-4xl mb-4">✅</div>
            <h2 className="text-2xl font-semibold mb-2 text-fontColor">
              Form Submitted!
            </h2>
            <p className="text-allfonts text-fontColor">
              Your response has been recorded successfully.
            </p>
            <button
              onClick={() => {
                setFormValues(buildInitialValues(normalizedFields));
                setErrors({});
                setTurnstileToken(null);
                setTurnstileKey((k) => k + 1);
                setSubmitted(false);
              }}
              className="mt-6 text-allfonts underline text-[#4381C1] hover:text-gray-800"
            >
              Reset form
            </button>
          </div>
        </div>
        <Footer bgColor="#F9FAFB" />
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // Main render
  // ─────────────────────────────────────────────
  return (
    <>
      {" "}
      {showLoader && (
        <LoadingDialog
          title="Submitting Information"
          primaryMSG="Processing your information..."
          bottomMsg="This will take a few seconds."
        />
      )}
      {/* ── Snackbar ── */}
      <Snackbar
        messages={snackbarMessages}
        onClose={handleCloseSnackbar}
        duration={6000}
        type={snackbarType}
      />{" "}
      <div className="h-screen flex flex-col bg-[#F9FAFB] overflow-hidden">
        {/* ── Phone dropdown: position:fixed so it escapes overflow-hidden parents ── */}
        <style>{`
        .react-tel-input .country-list {
          position: fixed !important;
          z-index: 9999 !important;
          max-height: 200px !important;
          overflow-y: auto !important;
          background-color: #ffffff !important;
          border: 1px solid #cccccc !important;
          border-radius: 6px !important;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
          padding: 4px 0 !important;
        }
        .react-tel-input .flag-dropdown.open {
          z-index: 9999;
        }
      `}</style>

        {showPhoneConfirmation && PhoneConfirmationModal}
        {/* ── Header ── */}
        <div
          className="p-4 px-4 border-b border-[#CCCCCC] bg-[#ffffff]"
          style={{ borderBottomWidth: "0.25px" }}
        >
          <Header />
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-4 bg-[#F9FAFB] top-1 max-h-[calc(100vh-110px)] mt-[16px] mb-[74px] w-full max-w-full">
          {/* ── Page intro ── */}
          <div className="pb-6">
            <h1 className="app-title font-medium mb-4 mt-2 text-fontColor">
              Welcome {authService.getCandidateName()}!
            </h1>
            <p className="font-medium text-lessHeroFontSize mb-2 text-fontColor">
              Before we get started, we need a few basic information from you.
              Please provide the following details.
            </p>
            <p className="text-allfonts-small text-fontColor mb-2">
              Please note, your data is safe! None of your Personal details will
              be used for any other purposes other than this Assessment. All
              Information is encrypted and does not leave our systems, ever.
            </p>
            <p className="text-allfonts-small text-fontColor">
              To know more, Please read our{" "}
              <button
                onClick={handleOpenPrivacy}
                className="underline cursor-pointer text-[#3428C7]"
              >
                Data Privacy Policy
              </button>{" "}
              and{" "}
              <button
                onClick={handleOpenTerms}
                className="underline cursor-pointer text-[#3428C7]"
              >
                Terms of Use
              </button>{" "}
              documents.
            </p>
          </div>

          {/* ── Form fields ── */}
          <div className="flex flex-col gap-4 w-full">
            {/* ── OTP CARD ── */}
            {/* ── 1. Phone Number ── */}
            <div
              className="flex items-start gap-3 sm:gap-4 border border-[#CCCCCC] rounded-xl bg-white p-3 sm:p-4 md:p-6 w-full md:w-[75%]"
              style={{ borderWidth: "0.25px" }}
            >
              <CountBox count={1} />
              <div className="flex-1 min-w-0">
                <p className="text-allfonts font-semibold tracking-wide text-fontColor mb-1">
                  Phone Number (Mobile)
                  <span className="text-red-500 ml-0.5">*</span>
                </p>
                <p className="text-allfonts-small italic text-fontColor">
                  * We will send a One-Time-PIN (OTP) to verify your Phone
                  Number.
                </p>
                <p className="text-allfonts-small italic mb-3 text-fontColor">
                  * Phone Number in this formate: +Country Code-Phone Number
                  (eg: +91-9999999999)
                </p>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    {/* Phone input — constrained width */}
                    <div
                      className="w-full sm:max-w-[450px]"
                      ref={phoneInputWrapperRef}
                    >
                      <PhoneInput
                        country={"in"}
                        value={phoneNumber}
                        onChange={(phone) => setPhoneNumber(phone)}
                        disabled={otpStep === "otp" || otpStep === "verified"}
                        inputStyle={{
                          border: "0px",
                          borderBottom: "1px solid grey",
                          borderRadius: "0px",
                          fontSize: "16px",
                          width: "100%",
                        }}
                        buttonStyle={{
                          border: "0px",
                          borderBottom: "1px solid grey",
                          borderRadius: "0px",
                        }}
                        containerStyle={{ width: "100%" }}
                        placeholder="Country Code-Phone Number"
                      />
                    </div>

                    {otpStep === "phone" && (
                      <button
                        type="button"
                        disabled={
                          !phoneNumber ||
                          phoneNumber.replace(/\D/g, "").length < 10
                        }
                        className="flex btn-size items-center justify-center px-3 py-[5.5px] whitespace-nowrap bg-btnBgColor text-white disabled:opacity-30 disabled:cursor-not-allowed w-full sm:w-auto"
                        onClick={() => {
                          if (
                            !phoneNumber ||
                            phoneNumber.replace(/\D/g, "").length < 10
                          )
                            return;
                          setShowPhoneConfirmation(true);
                        }}
                      >
                        <img
                          src={requestOtpImg}
                          alt="request otp"
                          className="mr-2"
                        />
                        <span>Request OTP</span>
                      </button>
                    )}
                  </div>

                  {/* OTP input */}
                  {otpStep === "otp" && (
                    <div className="flex flex-col gap-2 mt-2">
                      <div className="text-allfonts text-fontColor">
                        Enter 6-digit OTP sent to your phone:
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        {/* 6 individual OTP boxes — exact same as OTPInput in Welcome.js */}
                        <div className="flex gap-1.5 sm:gap-2">
                          {otpBoxes.map((digit, index) => (
                            <input
                              key={index}
                              ref={(el) => (otpInputRefs.current[index] = el)}
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              autoComplete={
                                index === 0 ? "one-time-code" : "off"
                              }
                              value={digit}
                              maxLength={1}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, "");

                                const newBoxes = [...otpBoxes];
                                newBoxes[index] = val.slice(-1);

                                setOtpBoxes(newBoxes);
                                setOtpValue(newBoxes.join(""));

                                if (val && index < 5) {
                                  otpInputRefs.current[index + 1]?.focus();
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Backspace") {
                                  e.preventDefault();

                                  const newBoxes = [...otpBoxes];

                                  if (otpBoxes[index]) {
                                    newBoxes[index] = "";
                                    setOtpBoxes(newBoxes);
                                    setOtpValue(newBoxes.join(""));
                                  } else if (index > 0) {
                                    newBoxes[index - 1] = "";
                                    setOtpBoxes(newBoxes);
                                    setOtpValue(newBoxes.join(""));

                                    otpInputRefs.current[index - 1]?.focus();
                                  }
                                } else if (e.key === "ArrowLeft" && index > 0) {
                                  otpInputRefs.current[index - 1]?.focus();
                                } else if (
                                  e.key === "ArrowRight" &&
                                  index < 5
                                ) {
                                  otpInputRefs.current[index + 1]?.focus();
                                }
                              }}
                              onPaste={(e) => {
                                e.preventDefault();

                                const paste = e.clipboardData
                                  .getData("text")
                                  .replace(/\D/g, "")
                                  .slice(0, 6);

                                const newBoxes = new Array(6).fill("");

                                paste.split("").forEach((char, i) => {
                                  newBoxes[i] = char;
                                });

                                setOtpBoxes(newBoxes);
                                setOtpValue(newBoxes.join(""));

                                if (paste.length < 6) {
                                  otpInputRefs.current[paste.length]?.focus();
                                } else {
                                  otpInputRefs.current[5]?.focus();
                                }
                              }}
                              onFocus={(e) => e.target.select()}
                              className="w-8 h-8 sm:w-11 sm:h-11 text-center text-sm sm:text-lg border-2 rounded-md focus:outline-none bg-white border-[#666666] placeholder:text-[#CCCCCC]"
                              style={{ borderWidth: "0.25px" }}
                            />
                          ))}
                        </div>
                        <button
                          type="button"
                          disabled={otpValue.length !== 6 || isOtpSending}
                          className="flex items-center justify-center px-3 py-[3.5px] bg-btnBgColor text-white btn-size disabled:cursor-not-allowed disabled:opacity-30 whitespace-nowrap"
                          onClick={async () => {
                            setIsOtpSending(true);
                            try {
                              await apiService.verifyOtp(
                                sessionStorage.getItem("userId"),
                                phoneNumber,
                                otpValue,
                              );
                              setOtpVerified(true);
                              setOtpStep("verified");
                            } catch (error) {
                              showSnackbar(
                                error.message ||
                                  "Invalid OTP. Please try again.",
                                "error",
                              );
                            } finally {
                              setIsOtpSending(false);
                            }
                          }}
                        >
                          <span>
                            {isOtpSending ? "Verifying..." : "Verify OTP"}
                          </span>
                          <img src={arrowImg} alt="arrow" className="ml-2" />
                        </button>
                      </div>
                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          onClick={() => {
                            setOtpStep("phone");
                            setOtpValue("");
                            setOtpBoxes(["", "", "", "", "", ""]);
                            setPhoneResendTimer(0);
                            if (phoneResendIntervalRef.current)
                              clearInterval(phoneResendIntervalRef.current);
                          }}
                          className="text-[#3428C7] text-sm underline w-fit"
                        >
                          Change Phone Number
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (phoneResendTimer > 0 || isOtpSending) return;
                            setIsOtpSending(true);
                            try {
                              await apiService.sendOtp(resumeId, phoneNumber);
                              showSnackbar(
                                "OTP resent to your phone number",
                                "success",
                              );
                              startPhoneResendTimer();
                            } catch (error) {
                              showSnackbar(
                                error.message || "Failed to resend OTP",
                                "error",
                              );
                            } finally {
                              setIsOtpSending(false);
                            }
                          }}
                          disabled={phoneResendTimer > 0 || isOtpSending}
                          className="text-[#3428C7] text-sm underline w-fit disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isOtpSending
                            ? "Sending..."
                            : phoneResendTimer > 0
                              ? `Resend OTP (${phoneResendTimer}s)`
                              : "Resend OTP"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Verified badge */}
                  {otpStep === "verified" && (
                    <div className="flex items-center gap-2 text-green-600">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15.275"
                        height="10.75"
                        viewBox="0 0 19.275 13.75"
                      >
                        <path
                          id="seen-svgrepo-com"
                          d="M2.421,12.833a1.382,1.382,0,0,1,1.953.033L8.8,17.451,18.993,7.381a1.382,1.382,0,0,1,1.934,1.965L9.749,20.4a1.382,1.382,0,0,1-.967.4H8.769a1.382,1.382,0,0,1-.978-.414l-5.4-5.6a1.382,1.382,0,0,1,.033-1.953Z"
                          transform="translate(-2 -7.047)"
                          fill="#099624"
                        />
                      </svg>
                      <span className="text-allfonts-small  text-fontColor  ">
                        Phone number verified successfully
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── REMAINING DYNAMIC FIELDS ── */}
            {otpVerified &&
              sortedFields.map((field, index) => (
                <div
                  id={`field-${field.id}`}
                  key={field.id}
                  className="flex items-start gap-3 sm:gap-4 border border-[#CCCCCC] rounded-xl bg-white p-3 sm:p-4 md:p-6 w-full md:w-[75%]"
                  style={{ borderWidth: "0.25px" }}
                >
                  <CountBox count={index + 2} />

                  <div className="flex-1 min-w-0">
                    {field.type !== "checkbox" && (
                      <>
                        <p className="text-allfonts font-semibold tracking-wide text-fontColor mb-1">
                          {field.label}
                          {field.required && (
                            <span className="text-red-500 ml-0.5">*</span>
                          )}
                        </p>
                        {field.description && (
                          <p className="text-allfonts-small italic mb-3 text-fontColor">
                            {field.description}
                          </p>
                        )}
                      </>
                    )}

                    {renderField(field)}

                    {errors[field.id] && (
                      <p className="text-red-500 text-allfonts-small mt-2">
                        {errors[field.id]}
                      </p>
                    )}
                  </div>
                </div>
              ))}

            {/* ── CONSENT CHECKBOX ── */}
            {otpVerified && (
              <div
                id="field-_consent"
                className="flex items-start gap-3 sm:gap-4 border border-[#CCCCCC] rounded-xl bg-white p-3 sm:p-4 md:p-6 w-full md:w-[75%]"
                style={{ borderWidth: "0.25px" }}
              >
                <div className="flex-1 min-w-0">
                  <label className="flex items-start space-x-2.5 text-allfonts cursor-pointer text-fontColor select-none">
                    <input
                      type="checkbox"
                      checked={consentGiven}
                      onChange={(e) => {
                        setConsentGiven(e.target.checked);
                        if (e.target.checked) {
                          setErrors((prev) => ({ ...prev, _consent: null }));
                        }
                      }}
                      className="transform scale-125 shrink-0"
                      style={{ accentColor: "#002147", marginTop: ".4rem" }}
                    />
                    <span>
                      By submitting this form, I consent that my personal
                      details can be stored.
                      <span className="text-red-500 ml-0.5">*</span>
                    </span>
                  </label>
                  {errors._consent && (
                    <p className="text-red-500 text-allfonts-small mt-2">
                      {errors._consent}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── CAPTCHA ── */}
            {otpVerified && (
              <div
                className="flex items-start gap-3 sm:gap-4 border border-[#CCCCCC] rounded-xl bg-white p-3 sm:p-4 md:p-6 w-full md:w-[75%]"
                style={{ borderWidth: "0.25px" }}
              >
                <div className="flex-1 min-w-0 overflow-x-auto">
                  <p className="text-allfonts text-fontColor mb-3">
                    {captchaMessage}
                  </p>
                  <Turnstile
                    key={turnstileKey}
                    siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => {
                      setTurnstileToken(token);
                      setCaptchaMessage(
                        "Verification successful. Please click on Submit.",
                      );
                      setErrors((prev) => ({ ...prev, _captcha: null }));
                    }}
                  />
                  {errors._captcha && (
                    <p className="text-red-500 text-allfonts-small mt-2">
                      {errors._captcha}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Fixed bottom action bar ── */}
        {otpVerified && (
          <div
            className="fixed bottom-0 left-0 w-full bg-[#F9FAFB] border-t border-[#cccccc] z-30 px-4 sm:px-6 py-2 flex items-center min-w-0"
            style={{ borderWidth: "0.25px" }}
          >
            <div className="text-[10px] sm:text-footerFontSize text-[#999999] break-words min-w-0 pr-2">
              ©{" "}
              {new Date().getFullYear() > 2026
                ? `2026-${new Date().getFullYear()}`
                : "2026"}
              , Needles and Haystacks Technology Pvt Ltd.
            </div>
            <div className="ml-auto shrink-0">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || showLoader || !consentGiven}
                className="flex text-[11px] sm:text-btnFontSize items-center px-4 sm:px-6 py-1.5 text-white bg-btnBgColor btn-size disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {dataReviewRequired ? "Submit" : "Schedule Your Assessment"}
                <img
                  src={arrowImg}
                  alt="arrow"
                  className="ml-2 w-[15px] h-[15px] sm:w-[16px] sm:h-[16px]"
                />
              </button>
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        {!otpVerified && <Footer bgColor="#F9FAFB" color="#999999" />}
      </div>
    </>
  );
}
